/**
 * UygulamaErisimService — platform uygulaması erişim kapısı (kullanıcı bazlı).
 * Süper yönetici → her şey. 'herkes' modu → serbest. 'kisitli'/'ucretli' → kullanıcının
 * goUyeler.veri.uygulamalar[] izninde olmalı. İzinler bir kez DB'den yüklenir (cache).
 */
import { Injectable, inject, signal } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { UYGULAMA_BUL, ALT_OZ_ANAHTAR } from './uygulama-katalog';
import { CihazLisansService } from './cihaz-lisans.service';
import { ModulService } from './modul.service';
import { PaketService } from './paket.service';
import { IErisimAyar, etkinSeviye, seviyeBedavaMi, katalogFiyatKalemleri } from './paketler';

@Injectable({ providedIn: 'root' })
export class UygulamaErisimService {
  private FikoYT = inject(FikoYTService);
  private req = inject(FikoYTRequestService);
  private cihaz = inject(CihazLisansService);
  private _modul = inject(ModulService);   // ModulService köprüsünü (window.__fikoModul) de tetikle
  private paket = inject(PaketService);
  private bildirim = inject(NzNotificationService);

  /** anahtar → kod fiyatı (kampanya bildiriminde "normalde ₺X" için). */
  private readonly kodFiyatHarita: Record<string, number> = (() => {
    const m: Record<string, number> = {};
    for (const g of katalogFiyatKalemleri()) for (const k of g.kalemler) m[k.anahtar] = k.kodFiyat;
    return m;
  })();
  private kampanyaBildirilen = new Set<string>();

  /** Bir kalem şu an kampanyada mı? Evetse fiyat + bitiş döner. */
  kampanyaBilgi(anahtar: string): { fiyat: number; bitis: string } | null {
    const ay = this.seviyeler()[anahtar];
    if (etkinSeviye(ay, new Date().toISOString()) !== 'kampanya') return null;
    return { fiyat: this.kodFiyatHarita[anahtar] || 0, bitis: this.tarihKisa(ay?.bitis) };
  }
  /** Kampanyalı kalem kullanılınca "normalde ücretli, şimdilik ücretsiz" bildir (oturumda 1 kez). */
  kampanyaBildir(anahtar: string, ad: string): void {
    if (this.kampanyaBildirilen.has(anahtar)) return;
    const b = this.kampanyaBilgi(anahtar); if (!b) return;
    this.kampanyaBildirilen.add(anahtar);
    const fiyatMetni = b.fiyat ? `normalde ${b.fiyat.toLocaleString('tr-TR')}₺/ay` : 'normalde ücretli';
    const sure = b.bitis ? ` ${b.bitis}'e kadar` : '';
    this.bildirim.success('🎁 Şimdilik Ücretsiz', `${ad}: ${fiyatMetni} — kampanya sayesinde${sure} ücretsiz kullanıyorsunuz!`, { nzDuration: 7000 });
  }
  private tarihKisa(iso?: string): string {
    if (!iso) return ''; try { const d = new Date(iso); return `${('0' + d.getDate()).slice(-2)}.${('0' + (d.getMonth() + 1)).slice(-2)}.${d.getFullYear()}`; } catch { return iso; }
  }

  /** 📢 baz erişim zorunlu mu (admin ayarı). Default false = paylaşım sayfası herkese açık, sadece premium kilitli. */
  private paylasimZorunlu = signal(false);
  /** Kalem erişim seviyeleri (kampanya/ücretsiz). anahtar → ayar. */
  private seviyeler = signal<Record<string, IErisimAyar>>({});
  private _seviyeYuklendi = false;
  private _seviyeYukleniyor: Promise<void> | null = null;
  /** Sadece erişim seviyelerini (kampanya/ücretsiz) HIZLI yükle — cihaz/kullanıcı izin yüklemesini bekletmez. */
  private async seviyelerHazir(): Promise<void> {
    if (this._seviyeYuklendi) return;
    if (!this._seviyeYukleniyor) {
      this._seviyeYukleniyor = (async () => {
        try { this.seviyeler.set((await this.paket.erisimSeviyeleri()) || {}); } catch { }
        this._seviyeYuklendi = true;
      })();
    }
    // En fazla 2.5s bekle — yavaş/yanıtsız sunucuda guard asla takılmaz.
    await Promise.race([this._seviyeYukleniyor, new Promise<void>(r => setTimeout(r, 2500))]);
  }

  /** Bir kalem (uyg / uygId:altId) şu an ücretsiz mi? herkes/kampanya → evet; uye → girişliyse evet. */
  bedavaMi(anahtar: string): boolean {
    const sv = etkinSeviye(this.seviyeler()[anahtar], new Date().toISOString());
    if (sv === 'uye') return !!this.aktifId();
    return seviyeBedavaMi(sv);
  }

  constructor() {
    // Lib (3D motoru) → app lisans köprüsü. Lib window.__fikoLisans('🌌','agirlik_olcu') ile sorar.
    // Köprü kurulmamışsa lib varsayılan AÇIK kabul eder (geriye uyumlu).
    try {
      (window as any).__fikoLisans = (uyg: string, alt?: string) =>
        alt ? this.altOzellikAcikSenk(uyg, alt) : this.erisebilirSenk(uyg);
      // Kampanya köprüleri: lib (3D motoru) kampanyalı komut/özellik kullanınca bildirir.
      (window as any).__fikoKampanya = (uyg: string, alt?: string) => this.kampanyaBilgi(alt ? ALT_OZ_ANAHTAR(uyg, alt) : uyg);
      (window as any).__fikoKampanyaBildir = (uyg: string, alt: string, ad: string) => this.kampanyaBildir(alt ? ALT_OZ_ANAHTAR(uyg, alt) : uyg, ad);
      this.izinleriYukle();   // arka planda izinleri çek
    } catch { }
  }

  /** Aktif kullanıcının izinli uygulama id'leri (null = henüz yüklenmedi). */
  private izinler = signal<string[] | null>(null);
  /** İzinli alt özellik anahtarları ("uygId:altId"). */
  private altlar = signal<string[]>([]);

  private aktifId(): string { return this.FikoYT.kimlik?.aktifKullaniciJson?.()?._id || ''; }
  private superMi(): boolean {
    const y = this.FikoYT.kimlik?.aktifKullaniciJson?.()?.yetkiler || [];
    return Array.isArray(y) && y.some((x: string) => x === 'Süper Yönetici' || x === 'Super Yonetici');
  }

  /** Uygulama izinlerini yükle: kullanıcı (goUyeler.veri) ∪ CİHAZ (buCihazLisans).
   *  Loginsiz kiosk (HMI) için kullanıcı yokken de CİHAZ izinleri yüklenir. */
  async izinleriYukle(): Promise<void> {
    const uid = this.aktifId();
    try {
      // Cihaz izinleri her durumda (loginsiz kiosk dahil); kullanıcı izinleri yalnız login varsa.
      const cl = await this.cihaz.buCihazLisans();
      let kUyg: string[] = [], kAlt: string[] = [];
      if (uid) {
        const r = await this.req.aggregate<any>('FikoYT.sistem.DB.goUyeler.aggregate', [
          { $match: { _id: uid } }, { $project: { 'veri.uygulamalar': 1, 'veri.uygulamaAltlari': 1 } }, { $limit: 1 },
        ]);
        kUyg = r?.[0]?.veri?.uygulamalar || [];
        kAlt = r?.[0]?.veri?.uygulamaAltlari || [];
      }
      this.izinler.set([...kUyg, ...(cl.uygulamalar || [])]);
      this.altlar.set([...kAlt, ...(cl.uygulamaAltlari || [])]);
      try { const ay = await this.paket.ayarlar(); this.paylasimZorunlu.set(ay.paylasimBazZorunlu); this.seviyeler.set(ay.erisimSeviyeleri || {}); this._seviyeYuklendi = true; } catch { }
    } catch { this.izinler.set([]); this.altlar.set([]); }
  }

  /** Uygulama içi alt özellik açık mı? (uygulamaya erişim + alt özellik izni) */
  async altOzellikAcik(uygId: string, altId: string): Promise<boolean> {
    if (this.superMi()) return true;
    await this.seviyelerHazir();
    if (this.bedavaMi(ALT_OZ_ANAHTAR(uygId, altId))) return true;   // kampanya/ücretsiz
    if (!(await this.erisebilir(uygId))) return false;        // önce uygulama erişimi
    if (this.izinler() === null) await this.izinleriYukle();
    return this.altlar().includes(ALT_OZ_ANAHTAR(uygId, altId));
  }
  /** Senkron alt özellik kontrolü (izinler yüklüyse). */
  altOzellikAcikSenk(uygId: string, altId: string): boolean {
    if (this.superMi()) return true;
    if (this.bedavaMi(ALT_OZ_ANAHTAR(uygId, altId))) return true;   // kampanya/ücretsiz
    if (!this.erisebilirSenk(uygId)) return false;
    return this.altlar().includes(ALT_OZ_ANAHTAR(uygId, altId));
  }

  /** Kullanıcı bu uygulamaya erişebilir mi? */
  async erisebilir(id: string): Promise<boolean> {
    if (this.superMi()) return true;
    const t = UYGULAMA_BUL(id);
    if (!t || t.mod === 'herkes') return true;            // katalogda yoksa veya serbest
    await this.seviyelerHazir();                          // HIZLI: kampanya/ücretsiz kontrolü için
    if (this.bedavaMi(id)) return true;                   // kampanya/ücretsiz → izin beklemeden aç
    if (this.izinler() === null) await this.izinleriYukle();
    if (id === '📢' && !this.paylasimZorunlu()) return true;  // baz erişim zorunlu değilse paylaşım açık
    return (this.izinler() || []).includes(id);
  }

  /** Senkron kontrol (izinler önceden yüklüyse) — launcher görsel için. */
  erisebilirSenk(id: string): boolean {
    if (this.superMi()) return true;
    const t = UYGULAMA_BUL(id);
    if (!t || t.mod === 'herkes') return true;
    if (this.bedavaMi(id)) return true;
    if (id === '📢' && !this.paylasimZorunlu()) return true;
    return (this.izinler() || []).includes(id);
  }

  /** İzin cache'ini sıfırla (admin değişiklik yapınca / kullanıcı değişince). */
  sifirla() { this.izinler.set(null); }
}
